/**
 * Typed registry of every public URL DebtDrone serves.
 *
 * This is the single source of truth for public route paths, locale, page type,
 * indexability, canonical target, page metadata and sitemap inclusion. The
 * sitemap, the prerender step, public navigation and the SEO tests all read
 * from here, so a route can never be present in one of them and missing from
 * another.
 *
 * Two rules keep this module consumable everywhere:
 *
 * 1. No `import.meta` and no `@/` alias imports. The Vite build config imports
 *    this module to emit the build-time route manifest, and the config is
 *    bundled outside the app's resolver.
 * 2. No content loading. Blog post URLs come from MDX frontmatter, which only
 *    the app bundle and the build script can read, so callers pass posts to
 *    `buildPublicRoutes`.
 *
 * ## Locale behaviour
 *
 * The landing page is the only translated route. English is the default locale
 * and is served at unprefixed paths (`/`, `/pricing`, `/legal/terms`); `es` and
 * `de` exist only as the prefixed landing variants `/es` and `/de`. There is no
 * `/en` URL — linking to one would create a duplicate of `/`.
 *
 * Every route is self-canonical. The three landing URLs form one hreflang group
 * (`en`, `es`, `de`, plus `x-default` pointing at `/`); every other route has no
 * alternates because it is only published in English.
 */

import deLanding from '../../locales/de/landing.json';
import enLanding from '../../locales/en/landing.json';
import esLanding from '../../locales/es/landing.json';

/**
 * Apex domain every public URL is canonicalised to.
 *
 * Apex, not `www`: one host per page, and the host the sitemap, canonical tags
 * and hreflang alternates all agree on. `assertApexOrigin` below keeps it that
 * way if anyone edits this.
 */
export const SITE_URL = 'https://debtdrone.net';

function assertApexOrigin(siteUrl: string): string {
  const url = new URL(siteUrl);
  if (url.protocol !== 'https:') throw new Error(`[public-routes] SITE_URL must be https: ${siteUrl}`);
  if (url.hostname.startsWith('www.')) {
    throw new Error(`[public-routes] SITE_URL must be the apex domain, not www: ${siteUrl}`);
  }
  if (siteUrl !== url.origin) {
    throw new Error(`[public-routes] SITE_URL must be a bare origin with no trailing slash: ${siteUrl}`);
  }
  return siteUrl;
}

assertApexOrigin(SITE_URL);

export const LOCALES = ['en', 'es', 'de'] as const;
export type Locale = (typeof LOCALES)[number];

/** Served at unprefixed paths; all other locales live under `/{locale}`. */
export const DEFAULT_LOCALE: Locale = 'en';

export type PublicPageType =
  | 'landing'
  | 'pricing'
  | 'docs'
  | 'roadmap'
  | 'changelog'
  | 'blog-index'
  | 'blog-post'
  | 'guide'
  | 'sample-report'
  | 'legal';

export type ChangeFrequency = 'always' | 'hourly' | 'daily' | 'weekly' | 'monthly' | 'yearly' | 'never';

export interface RouteAlternate {
  hreflang: string;
  path: string;
}

export type SitemapPolicy =
  | { include: false }
  | { include: true; changefreq: ChangeFrequency; priority: number };

export interface RouteMetadata {
  title: string;
  description: string;
}

export interface PublicRoute {
  /** URL as served, absolute and without a trailing slash (except `/`). */
  path: string;
  /** React Router pattern in App.tsx that renders this URL. */
  routePattern: string;
  pageType: PublicPageType;
  locale: Locale;
  /** Whether search engines may index this URL. */
  indexable: boolean;
  /** Path this URL declares as its canonical. Every route is self-canonical today. */
  canonicalPath: string;
  /** hreflang group this URL belongs to. Empty when the page has no translations. */
  alternates: RouteAlternate[];
  metadata: RouteMetadata;
  sitemap: SitemapPolicy;
  /** ISO date (YYYY-MM-DD) for dated content. Only blog posts carry one. */
  publishedAt?: string;
}

export type ExclusionReason =
  | 'authenticated'
  | 'auth-flow'
  | 'oauth-callback'
  | 'share-link'
  | 'duplicate-path';

/** A route App.tsx serves that is deliberately not public: never indexed, prerendered or listed. */
export interface ExcludedRoute {
  /** React Router pattern in App.tsx. `:param` matches exactly one segment. */
  path: string;
  reason: ExclusionReason;
  note: string;
  /**
   * Whether the route owns everything beneath it as well. Only true where
   * App.tsx nests further routes under the pattern, so an unknown descendant of
   * a leaf route stays a genuine 404 rather than a soft one.
   */
  subtree?: boolean;
}

/**
 * Every non-public route pattern App.tsx declares at the top level, with why it
 * stays out of the registry. Nested children (`/admin/users`, …) are covered by
 * their excluded parent.
 */
export const EXCLUDED_ROUTES: readonly ExcludedRoute[] = [
  { path: '/login', reason: 'auth-flow', note: 'Sign-in form; redirects authenticated visitors away.' },
  { path: '/register', reason: 'auth-flow', note: 'Sign-up form.' },
  { path: '/signup', reason: 'duplicate-path', note: 'Renders the same page as /register.' },
  { path: '/waitlist', reason: 'auth-flow', note: 'Waitlist capture form shown in waitlist mode.' },
  { path: '/verify-email', reason: 'auth-flow', note: 'Consumes a one-time email token.' },
  { path: '/forgot-password', reason: 'auth-flow', note: 'Password reset request form.' },
  { path: '/reset-password', reason: 'auth-flow', note: 'Consumes a one-time reset token.' },
  { path: '/dashboard', reason: 'authenticated', note: 'Application home.' },
  { path: '/settings', reason: 'authenticated', note: 'Account and integration settings.' },
  { path: '/settings/trello-callback', reason: 'oauth-callback', note: 'Trello OAuth redirect target.' },
  { path: '/settings/team', reason: 'authenticated', note: 'Organization member management.' },
  { path: '/billing', reason: 'authenticated', note: 'Subscription and invoices.' },
  { path: '/profile', reason: 'authenticated', note: 'User profile.' },
  { path: '/scans', reason: 'authenticated', note: 'Scan history.' },
  { path: '/repositories', reason: 'authenticated', note: 'Connected repositories.' },
  { path: '/repositories/:id', reason: 'authenticated', note: 'Repository detail.' },
  { path: '/advisor', reason: 'authenticated', note: 'Advisor workspace.' },
  { path: '/advisor/:repositoryId', reason: 'authenticated', note: 'Advisor project: a new chat.' },
  { path: '/advisor/:repositoryId/:conversationId', reason: 'authenticated', note: 'Advisor saved chat.' },
  { path: '/issues', reason: 'authenticated', note: 'Issue inbox.' },
  { path: '/issues/:id', reason: 'authenticated', note: 'Issue detail.' },
  { path: '/reports', reason: 'authenticated', note: 'Report list.' },
  { path: '/reports/:id', reason: 'authenticated', note: 'Report detail.' },
  { path: '/notifications', reason: 'authenticated', note: 'Notification centre.' },
  { path: '/organizations/new', reason: 'authenticated', note: 'Organization creation flow.' },
  { path: '/mcp/authorize', reason: 'authenticated', note: 'MCP client consent; consumes a short-lived authorization request.' },
  { path: '/share/reports/:token', reason: 'share-link', note: 'Unlisted report preview behind an unguessable token.' },
  {
    path: '/admin',
    reason: 'authenticated',
    note: 'Admin console and every route nested under it.',
    subtree: true,
  },
];

/** Shared by the three landing URLs so they advertise one hreflang group. */
const LANDING_ALTERNATES: RouteAlternate[] = [
  { hreflang: 'en', path: '/' },
  { hreflang: 'es', path: '/es' },
  { hreflang: 'de', path: '/de' },
  { hreflang: 'x-default', path: '/' },
];

function fail(message: string): never {
  throw new Error(`[public-routes] ${message}`);
}

/**
 * Rejects a registry that would emit broken SEO artifacts: duplicate or
 * relative paths, blank metadata, sitemap entries that are not indexable,
 * canonical or hreflang targets that are not themselves public routes, and
 * overlap with the excluded list. Throws so a bad registry fails the build and
 * the test suite instead of silently shipping.
 */
export function validatePublicRoutes(routes: readonly PublicRoute[]): readonly PublicRoute[] {
  const paths = new Set<string>();

  for (const route of routes) {
    if (!route.path.startsWith('/')) fail(`route path must be absolute: "${route.path}"`);
    if (route.path !== '/' && route.path.endsWith('/')) fail(`route path must not end in a slash: "${route.path}"`);
    if (paths.has(route.path)) fail(`duplicate route path: "${route.path}"`);
    paths.add(route.path);

    if (!route.routePattern.startsWith('/')) fail(`route pattern must be absolute: "${route.routePattern}"`);
    if (!route.metadata.title.trim()) fail(`missing title for "${route.path}"`);
    if (!route.metadata.description.trim()) fail(`missing description for "${route.path}"`);

    if (route.locale !== DEFAULT_LOCALE && !route.path.startsWith(`/${route.locale}`)) {
      fail(`"${route.path}" is a ${route.locale} route but is not served under /${route.locale}`);
    }

    if (route.sitemap.include) {
      if (!route.indexable) fail(`"${route.path}" is in the sitemap but is not indexable`);
      const { priority } = route.sitemap;
      if (!(priority >= 0 && priority <= 1)) fail(`sitemap priority for "${route.path}" must be between 0 and 1`);
    }
  }

  // Cross-route checks, once every public path is known.
  for (const route of routes) {
    if (!paths.has(route.canonicalPath)) {
      fail(`canonical target "${route.canonicalPath}" of "${route.path}" is not a public route`);
    }
    const seen = new Set<string>();
    for (const alternate of route.alternates) {
      if (seen.has(alternate.hreflang)) fail(`duplicate hreflang "${alternate.hreflang}" on "${route.path}"`);
      seen.add(alternate.hreflang);
      if (!paths.has(alternate.path)) {
        fail(`hreflang alternate "${alternate.path}" of "${route.path}" is not a public route`);
      }
    }
    if (route.alternates.length > 0 && !seen.has('x-default')) {
      fail(`"${route.path}" declares hreflang alternates without an x-default`);
    }
  }

  // Unique titles and descriptions: two indexable pages competing on identical
  // metadata is the duplicate-content problem the registry exists to prevent.
  for (const field of ['title', 'description'] as const) {
    const seen = new Map<string, string>();
    for (const route of routes) {
      if (!route.indexable) continue;
      const value = route.metadata[field];
      const owner = seen.get(value);
      if (owner) fail(`"${route.path}" and "${owner}" share the same ${field}`);
      seen.set(value, route.path);
    }
  }

  const excludedPaths = new Set<string>();
  for (const excluded of EXCLUDED_ROUTES) {
    if (excludedPaths.has(excluded.path)) fail(`duplicate excluded route: "${excluded.path}"`);
    excludedPaths.add(excluded.path);
    if (paths.has(excluded.path)) fail(`"${excluded.path}" is listed as both public and excluded`);
  }

  return routes;
}

/**
 * Every public route with a path known ahead of build time, keyed so pages can
 * reference their own route by name instead of re-typing a path string. Blog
 * posts are appended by `buildPublicRoutes` from MDX frontmatter.
 */
export const PUBLIC_PAGES = {
  landingEn: {
    path: '/',
    routePattern: '/',
    pageType: 'landing',
    locale: 'en',
    indexable: true,
    canonicalPath: '/',
    alternates: LANDING_ALTERNATES,
    metadata: {
      title: 'DebtDrone – Technical Debt Analysis for Engineering Teams',
      // Sourced from the hero, like the es and de entries below. The previous
      // wording claimed prioritisation by impact, which the scanner does not do.
      description: enLanding.hero.subtitle,
    },
    sitemap: { include: true, changefreq: 'weekly', priority: 1.0 },
  },
  landingEs: {
    path: '/es',
    routePattern: '/es',
    pageType: 'landing',
    locale: 'es',
    indexable: true,
    canonicalPath: '/es',
    alternates: LANDING_ALTERNATES,
    metadata: {
      title: 'DebtDrone – Análisis de Deuda Técnica para Equipos de Ingeniería',
      // Every landing locale takes its description from its own hero subtitle.
      description: esLanding.hero.subtitle,
    },
    sitemap: { include: true, changefreq: 'weekly', priority: 0.9 },
  },
  landingDe: {
    path: '/de',
    routePattern: '/de',
    pageType: 'landing',
    locale: 'de',
    indexable: true,
    canonicalPath: '/de',
    alternates: LANDING_ALTERNATES,
    metadata: {
      title: 'DebtDrone – Analyse technischer Schulden für Engineering-Teams',
      description: deLanding.hero.subtitle,
    },
    sitemap: { include: true, changefreq: 'weekly', priority: 0.9 },
  },
  pricing: {
    path: '/pricing',
    routePattern: '/pricing',
    pageType: 'pricing',
    locale: 'en',
    indexable: true,
    canonicalPath: '/pricing',
    alternates: [],
    metadata: {
      title: 'Pricing – DebtDrone',
      description:
        "Simple, transparent pricing for automated technical debt scanning. Choose the plan that fits your team's velocity.",
    },
    sitemap: { include: true, changefreq: 'monthly', priority: 0.8 },
  },
  docs: {
    path: '/docs',
    routePattern: '/docs',
    pageType: 'docs',
    locale: 'en',
    indexable: true,
    canonicalPath: '/docs',
    alternates: [],
    metadata: {
      title: 'Documentation – DebtDrone',
      description:
        'Guides and reference docs for setting up DebtDrone: GitHub integration, scans, issue triage, billing, and API keys.',
    },
    sitemap: { include: true, changefreq: 'weekly', priority: 0.7 },
  },
  technicalDebtScanner: {
    path: '/technical-debt-scanner',
    routePattern: '/technical-debt-scanner',
    pageType: 'guide',
    locale: 'en',
    indexable: true,
    canonicalPath: '/technical-debt-scanner',
    alternates: [],
    metadata: {
      title: 'What a Technical Debt Scanner Does – DebtDrone',
      description:
        'How technical debt scanners work, what they can and cannot measure, and how to evaluate one against your own repository before you commit to it.',
    },
    sitemap: { include: true, changefreq: 'monthly', priority: 0.7 },
  },
  ciCdQualityGates: {
    path: '/ci-cd-quality-gates',
    routePattern: '/ci-cd-quality-gates',
    pageType: 'guide',
    locale: 'en',
    indexable: true,
    canonicalPath: '/ci-cd-quality-gates',
    alternates: [],
    metadata: {
      title: 'CI/CD Quality Gates That Teams Keep – DebtDrone',
      description:
        'How to add a code-quality gate to a pipeline, pick a threshold that holds, and avoid the failure modes that get gates switched off within a month.',
    },
    sitemap: { include: true, changefreq: 'monthly', priority: 0.7 },
  },
  sampleReport: {
    path: '/sample-report',
    routePattern: '/sample-report',
    pageType: 'sample-report',
    locale: 'en',
    indexable: true,
    canonicalPath: '/sample-report',
    alternates: [],
    metadata: {
      title: 'Sample Technical Debt Report – DebtDrone',
      description: 'Explore a clearly labeled synthetic DebtDrone report with actionable findings, scan context, and an explanation of effort estimates before you sign up.',
    },
    sitemap: { include: true, changefreq: 'monthly', priority: 0.7 },
  },
  roadmap: {
    path: '/roadmap',
    routePattern: '/roadmap',
    pageType: 'roadmap',
    locale: 'en',
    indexable: true,
    canonicalPath: '/roadmap',
    alternates: [],
    metadata: {
      title: 'Roadmap – DebtDrone',
      description:
        "See what's shipped, in progress, and planned for DebtDrone's automated technical debt hunting platform.",
    },
    sitemap: { include: true, changefreq: 'weekly', priority: 0.5 },
  },
  blogIndex: {
    path: '/blog',
    routePattern: '/blog',
    pageType: 'blog-index',
    locale: 'en',
    indexable: true,
    canonicalPath: '/blog',
    alternates: [],
    metadata: {
      title: 'Blog – DebtDrone',
      description: "Notes on DebtDrone's features, architecture, and how we think about hunting technical debt.",
    },
    sitemap: { include: true, changefreq: 'weekly', priority: 0.6 },
  },
  changelog: {
    path: '/changelog',
    routePattern: '/changelog',
    pageType: 'changelog',
    locale: 'en',
    indexable: true,
    canonicalPath: '/changelog',
    alternates: [],
    metadata: {
      title: 'Changelog – DebtDrone',
      description: 'What shipped, when — new features and fixes across DebtDrone.',
    },
    sitemap: { include: true, changefreq: 'weekly', priority: 0.5 },
  },
  legalPrivacy: {
    path: '/legal/privacy',
    routePattern: '/legal/privacy',
    pageType: 'legal',
    locale: 'en',
    indexable: true,
    canonicalPath: '/legal/privacy',
    alternates: [],
    metadata: {
      title: 'Privacy Policy – DebtDrone',
      description: 'How DebtDrone collects, uses, and protects your data.',
    },
    sitemap: { include: true, changefreq: 'yearly', priority: 0.3 },
  },
  legalTerms: {
    path: '/legal/terms',
    routePattern: '/legal/terms',
    pageType: 'legal',
    locale: 'en',
    indexable: true,
    canonicalPath: '/legal/terms',
    alternates: [],
    metadata: {
      title: 'Terms of Service – DebtDrone',
      description: "The terms governing your use of DebtDrone's automated technical debt analysis service.",
    },
    sitemap: { include: true, changefreq: 'yearly', priority: 0.3 },
  },
  legalRefund: {
    path: '/legal/refund',
    routePattern: '/legal/refund',
    pageType: 'legal',
    locale: 'en',
    indexable: true,
    canonicalPath: '/legal/refund',
    alternates: [],
    metadata: {
      title: 'Refund Policy – DebtDrone',
      description: 'When and how you can request a refund for a DebtDrone subscription purchased through Paddle.',
    },
    sitemap: { include: true, changefreq: 'yearly', priority: 0.3 },
  },
} satisfies Record<string, PublicRoute>;

/** The landing page is the only translated route; every other page is English-only. */
export const LANDING_ROUTES_BY_LOCALE: Record<Locale, PublicRoute> = {
  en: PUBLIC_PAGES.landingEn,
  es: PUBLIC_PAGES.landingEs,
  de: PUBLIC_PAGES.landingDe,
};

export const STATIC_PUBLIC_ROUTES: readonly PublicRoute[] = validatePublicRoutes(Object.values(PUBLIC_PAGES));

/**
 * A blog slug becomes both a URL segment and a path under `build/`, so it must
 * be a single safe segment: no separators, traversal, query strings, fragments,
 * whitespace, or percent-encoding that could resolve outside `/blog/`.
 */
const SLUG_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

/** The frontmatter fields a blog post contributes to its public route. */
export interface BlogPostSummary {
  title: string;
  slug: string;
  excerpt: string;
  /** Publication date from the post's frontmatter, as `YYYY-MM-DD`. */
  date: string;
}

export function blogPostRoute(post: BlogPostSummary): PublicRoute {
  // Checked here rather than in `validatePublicRoutes`, which would only see
  // the decorated title and never notice an empty one.
  if (!SLUG_PATTERN.test(post.slug)) {
    fail(
      `blog post slug ${JSON.stringify(post.slug)} must be a single URL segment of ` +
        'lowercase letters, numbers and hyphens',
    );
  }
  if (!post.title.trim()) fail(`blog post "${post.slug}" has no title`);
  if (!post.excerpt.trim()) fail(`blog post "${post.slug}" has no excerpt`);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(post.date)) {
    fail(`blog post "${post.slug}" has date ${JSON.stringify(post.date)}, expected YYYY-MM-DD`);
  }

  return {
    path: `/blog/${post.slug}`,
    routePattern: '/blog/:slug',
    pageType: 'blog-post',
    locale: DEFAULT_LOCALE,
    indexable: true,
    canonicalPath: `/blog/${post.slug}`,
    alternates: [],
    metadata: {
      title: `${post.title} – DebtDrone Blog`,
      description: post.excerpt,
    },
    sitemap: { include: true, changefreq: 'monthly', priority: 0.5 },
    publishedAt: post.date,
  };
}

/** The complete public registry: static routes plus one route per published post. */
export function buildPublicRoutes(posts: readonly BlogPostSummary[]): readonly PublicRoute[] {
  return validatePublicRoutes([...STATIC_PUBLIC_ROUTES, ...posts.map(blogPostRoute)]);
}


/** Static file the prerender step writes under `build/` for a route. */
export function prerenderFilePath(path: string): string {
  return path === '/' ? 'index.html' : `${path.slice(1)}.html`;
}
